import React from "react";

import { ChevronDown, ChevronRight } from "lucide-react";

import {
  ACCENT,
  CARD_SURFACE,
  ColumnHeading,
  MoveList,
  PointList,
  QuoteBand,
  ScoreStrip,
  Section,
} from "./Chrome";
import type { XFactorDigestData } from "../../../Types/XFactorBriefing";
import { SourceList } from "../../XFactorBriefings/components/Primitives";
import { buildSituationCard } from "../lib/derive";

/**
 * Step 1 of the ExecBridge flow.
 *
 * The one-screen read: four scores, the narrative, the account motion as a
 * pull-quote, then Must Know / Watch-Outs / Moves side by side, and the outlook.
 */
const SituationCard: React.FC<{
  accountName: string;
  data?: XFactorDigestData | null;
}> = ({ accountName, data }) => {
  const card = React.useMemo(
    () => buildSituationCard(accountName, data),
    [accountName, data]
  );
  const [sourcesOpen, setSourcesOpen] = React.useState(false);

  if (!card.present) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <span className="font-medium">
          This briefing carries no Situation Card.
        </span>{" "}
        xfactor&apos;s synthesis pass writes the card into the payload; its
        absence means the pass did not complete for this digest. The Account
        Synopsis reads the sections directly and may still have content.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        {card.freshness ? (
          <span className="rounded bg-gray-100 px-2 py-0.5">
            Freshness: {card.freshness}
          </span>
        ) : null}
        {card.confidence != null ? (
          <span className="rounded bg-gray-100 px-2 py-0.5">
            Confidence: {Math.round(card.confidence)}%
          </span>
        ) : null}
      </div>

      <ScoreStrip tiles={card.tiles} />

      {card.narrative ? (
        <div
          className={`${CARD_SURFACE} mt-4 flex items-start justify-between gap-3 px-3 py-2.5`}
        >
          <p className="text-sm leading-relaxed text-gray-700">
            {card.narrative}
          </p>
        </div>
      ) : null}

      {card.quote ? (
        <div className="mt-4">
          <QuoteBand>{card.quote}</QuoteBand>
        </div>
      ) : null}

      <div className="mt-6 grid gap-5 lg:grid-cols-3">
        <div>
          <ColumnHeading tone="text-gray-500">Must know</ColumnHeading>
          <PointList points={card.mustKnow} citations={card.citations} />
        </div>
        <div>
          <ColumnHeading tone="text-red-500">Watch-outs</ColumnHeading>
          <PointList points={card.watchOuts} citations={card.citations} />
        </div>
        <div>
          {/* Same muted category tone as Must know (not darker). */}
          <ColumnHeading tone="text-gray-500">
            Recommended actions and owners
          </ColumnHeading>
          <MoveList moves={card.moves} />
        </div>
      </div>

      {card.outlook ? (
        <Section title="Outlook">
          <p className="text-sm leading-relaxed text-gray-700">
            {card.outlook}
          </p>
        </Section>
      ) : null}

      {card.citations.length ? (
        <div className="mt-6 rounded-lg border border-gray-200">
          <button
            type="button"
            onClick={() => setSourcesOpen(o => !o)}
            aria-expanded={sourcesOpen}
            className="flex w-full items-center justify-between px-4 py-2.5"
          >
            <span
              className="text-[11px] font-semibold uppercase tracking-wide"
              style={{ color: ACCENT }}
            >
              Sources ({card.citations.length})
            </span>
            {sourcesOpen ? (
              <ChevronDown size={14} className="text-gray-400" aria-hidden />
            ) : (
              <ChevronRight size={14} className="text-gray-400" aria-hidden />
            )}
          </button>
          {sourcesOpen ? (
            <div className="border-t border-gray-100 px-4 py-3">
              <SourceList citations={card.citations} />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default SituationCard;
